import i18n from 'i18n-js';
import CampaignGroupSearchView from 'omega-rtc/views/ucd/CampaignGroupSearchView';
import { CAMPAIGN_GROUP_TAB } from 'omega-rtc/utils/campaign-group-search';
import { useJourneyCampaignGroupEnabled } from 'omega-rtc/utils/portal-config';
import { Alert, Flex, Tabs, Typography } from '@duxton-web/ui';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';
import DocumentTitle from 'react-document-title';

import { getUCDConfigs } from 'omega-rtc/actions/ucd';
import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';
import {
  CURRENT_TAB_QUERY_KEY,
  UCD_ACTION_TYPES,
  UCD_RESOURCE_NAMES,
} from 'omega-rtc/constants/ucd';

import CampaignSearchView from 'omega-rtc/views/ucd/CampaignSearchView';
import ContentGenerationView from 'omega-rtc/views/ucd/ContentGenerationView';
import ContentConfigPortal from 'omega-rtc/views/ucd/content-config/ContentConfigPortal';
import { getContentConfigPermissions } from 'omega-rtc/views/ucd/content-config/permissions';
import ProjectSearchView from 'omega-rtc/views/ucd/ProjectSearchView';
import './UCDHomePage.scss';
import CreateCampaignProjectButton from 'omega-rtc/components/custom/CreateCampaignProjectButton';

const TAB_PROJECTS_KEY = 'projects';
const TAB_CAMPAIGNS_KEY = 'campaigns';
const TAB_CONTENT_GENERATION_KEY = 'content-generation';
const TAB_CONTENT_CONFIG_KEY = 'content-config';
const UCD_HOME_PATH = '/ucd';
const isContentConfigPortalEnabled = () => _.get(window, 'Grab.config.ENABLE_CONTENT_CONFIG_PORTAL', false) === true;
const unauthorizedContent = (
  <Alert message="You don't have access to Holos" showIcon type="error" />
);

export const resolveUcdRequestedTab = (
  searchParams: Record<string, string> = {},
) => {
  const queryTab = _.get(searchParams, CURRENT_TAB_QUERY_KEY);

  return queryTab || TAB_PROJECTS_KEY;
};

export const buildUcdTabUrl = (
  searchParams: Record<string, string> = {},
  currentTab: string,
) => {
  const paramsObj = new URLSearchParams(searchParams);
  paramsObj.set(CURRENT_TAB_QUERY_KEY, currentTab);

  return `${UCD_HOME_PATH}?${paramsObj.toString()}`;
};

interface Props {
  location: {
    query: Record<string, string>;
    pathname: string;
  };
  router: any;
}

const UCDHomePage = (props: Props) => {
  const user = useSelector((state: any) => state.app.user);
  const { location, router } = props;
  const searchParams = location.query;

  const dispatch = useDispatch();
  const campaignGroupEnabled = useJourneyCampaignGroupEnabled();
  const contentConfigEnabled = isContentConfigPortalEnabled();
  const userPermissions = _.get(user, 'userPermissions');
  const hasUserPermissions = Boolean(userPermissions);
  const contentConfigPermissions = getContentConfigPermissions(userPermissions);
  const contentConfigVisible = contentConfigEnabled && contentConfigPermissions.canView;
  const queryTab = resolveUcdRequestedTab(searchParams);
  const shouldRepairContentConfigTab = queryTab === TAB_CONTENT_CONFIG_KEY
    && hasUserPermissions
    && !contentConfigVisible;
  const activeTab = queryTab === TAB_CONTENT_CONFIG_KEY && !contentConfigVisible
    ? TAB_PROJECTS_KEY
    : queryTab;

  const directCampaignGroupAccess = !campaignGroupEnabled && activeTab === CAMPAIGN_GROUP_TAB;

  useEffect(() => {
    dispatch(getUCDConfigs());
  }, [dispatch]);

  // Keep tab URLs canonical under /ucd?tab=... and repair coerced tabs when
  // the requested tab is unavailable.
  useEffect(() => {
    if (shouldRepairContentConfigTab) {
      browserHistory.replace(buildUcdTabUrl(searchParams, activeTab));
    }
  }, [activeTab, searchParams, shouldRepairContentConfigTab]);

  const handleTabClick = (currentTab: string) => {
    if (currentTab !== activeTab) {
      // Drop the content-config share params (?country=..&key=.. from an open
      // Product Offering) — they describe a view that leaves the screen on tab
      // switch and would otherwise be carried into the new tab's URL.
      const { country, key, ...restParams } = searchParams;
      browserHistory.push(buildUcdTabUrl(restParams, currentTab));
    }
  };

  const tabsMeta = [
    {
      key: TAB_PROJECTS_KEY,
      label: 'Projects',
      children: (
        <ProjectSearchView searchParams={searchParams} router={router} />
      ),
    },
    {
      key: TAB_CAMPAIGNS_KEY,
      label: 'Campaigns',
      children: (
        <CampaignSearchView
          searchParams={searchParams}
          router={router}
          user={user}
        />
      ),
    },
    ...(campaignGroupEnabled ? [{
      key: CAMPAIGN_GROUP_TAB,
      label: i18n.t('campaign_group_search.tab'),
      children: <CampaignGroupSearchView searchParams={searchParams} router={router} />,
    }] : []),
    {
      key: TAB_CONTENT_GENERATION_KEY,
      label: 'Content Generation',
      children: <ContentGenerationView />,
    },
    ...(contentConfigVisible ? [{
      key: TAB_CONTENT_CONFIG_KEY,
      label: 'Content Config',
      children: <ContentConfigPortal contentConfigPermissions={contentConfigPermissions} />,
    }] : []),
  ];

  return (
    <DocumentTitle title="Holos">
      <div className={'ucd-basic'}>
        <Flex
          vertical={false}
          justify="space-between"
          style={{
            paddingBottom: '30px',
          }}
        >
          <Typography.Title level={2}>Unified campaigns</Typography.Title>
          <CreateCampaignProjectButton router={props.router} />
        </Flex>
        <div className="ucd-nav">
          <Tabs
            type="pill"
            onTabClick={handleTabClick}
            activeKey={activeTab}
            items={directCampaignGroupAccess
              ? tabsMeta.map((tab) => ({ ...tab, children: null }))
              : tabsMeta}
          />
          {/* The rollout flag controls discovery only, like Create Campaign Group.
              Direct URLs remain inside the existing Holos authorization boundary. */}
          {directCampaignGroupAccess && (
            <CampaignGroupSearchView searchParams={searchParams} router={router} />
          )}
        </div>
      </div>
    </DocumentTitle>
  );
};

// page-level access control
const UCDHomePageComponent = (props: Props) => (
  <ShowIfAuthorized
    conditions={[
      {
        resource: UCD_RESOURCE_NAMES.campaign,
        action: UCD_ACTION_TYPES.read,
      },
      {
        resource: UCD_RESOURCE_NAMES.project,
        action: UCD_ACTION_TYPES.read,
      },
    ]}
    unauthorizedContent={unauthorizedContent}
  >
    <UCDHomePage {...props} />
  </ShowIfAuthorized>
);

export default UCDHomePageComponent;
